import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material';
import { AxiosError } from 'axios';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { unwrapResult } from '@reduxjs/toolkit';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLogo } from '../../config/useLogo';
import { AUTO_LOGIN_ROUTE } from '../../routes/namedRoutes';
import { ImproveIt360SolutionOrgName } from '../../common/constants';
import improveIt360Logo from '../../assets/images/improve_it_360_logo.svg';
import { useSolutionOrg } from '../../config/useSolutionOrg';
import OptimusInput from '../../common/library/Input/Input';
import OptimusButton from '../../common/library/Button/Button';
import { Logger } from '../../config/logging/logger';
import { publicMemberValidation, validateRegistrationToken, memberRegistration } from '../../api/loginAPI';
import { TestIds } from '../../assets/testIds';
import {
  selectOnboardingForm,
  selectGetOnboardingFormStatus,
} from '../../store/slices/onboardingSlice';
import { QuestionOption } from '../../common/models/Question';
import { RegistrationSource } from '../../common/enums';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useCustomEventTracking } from '../../common/useCustomEventTracking';
import QuestionComponent from '../../common/QuestionComponent';
import { getOnboardingForm } from '../../store/thunks/onboardingThunk';
import { ONBOARDING_FORMS } from '../Onboarding/constants';
import { useOnboardingRedirectPath } from '../../common/useOnboardingRedirectPath';

type RegistrationCompleteProps = {
  registrationCode: string;
  memberEmail: string;
  // Prefill carried on the registration link from the marketing sign up form. Absent when the
  // registration started from a surface that only collects an email.
  firstName?: string;
  lastName?: string;
  companyName?: string;
};

const DEFAULT_COUNTRY = 'US';

export function RegistrationComplete({
  registrationCode,
  memberEmail,
  firstName,
  lastName,
  companyName,
}: RegistrationCompleteProps) {
  const decodedEmail = useMemo(
    () => decodeURIComponent(memberEmail || '').trim().toLowerCase(),
    [memberEmail],
  );

  const isRegistrationCodeValid = registrationCode?.trim().length === 6;
  const linkIsInvalid = !decodedEmail || !isRegistrationCodeValid;

  const [searchParams] = useSearchParams();
  const solutionOrg = searchParams.get('solutionOrg');
  const { secondaryOne: Logo } = useLogo();
  const { solutionOrgName } = useSolutionOrg();
  const isImproveIt360 = solutionOrgName === ImproveIt360SolutionOrgName;
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const logger = useMemo(() => Logger.create('PublicRegistration'), []);
  const onboardingRedirectPath = useOnboardingRedirectPath();
  const { trackSignUp } = useCustomEventTracking();
  const { trackRequest } = useCustomEventTracking();
  const onboardingForm = useAppSelector(selectOnboardingForm);
  const businessInformationQuestions = useMemo(
    () => onboardingForm.questions || [],
    [onboardingForm.questions],
  );
  const getOnboardingFormStatus = useAppSelector(selectGetOnboardingFormStatus);
  // Seeded from the link so the member does not retype what marketing already collected. The
  // fields stay editable, and anything the link did not carry starts empty as before.
  const [formValues, setFormValues] = useState({
    firstName: firstName?.trim() || '',
    lastName: lastName?.trim() || '',
    companyName: companyName?.trim() || '',
    trade: '',
  });
  const businessMainIndustryTitle = 'Business or trade';

  const [formErrors, setFormErrors] = useState<Partial<typeof formValues>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSuccessSnackbar, setOpenSuccessSnackbar] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [emailResent, setEmailResent] = useState(false);
  const hasValidatedRef = useRef(false);
  const businessMainIndustryQuestion = businessInformationQuestions?.find(
    (question) => question.title === businessMainIndustryTitle,
  );
  const businessMainIndustryOptions = businessMainIndustryQuestion?.options || [];
  const canSelectBusinessTrade = businessMainIndustryOptions.length > 0;
  const tradePlaceholderKey = useMemo(() => {
    if (businessMainIndustryQuestion?.title === businessMainIndustryTitle) {
      return 'businessOrTradePlaceholderV2';
    }
    return businessMainIndustryQuestion?.title || businessMainIndustryTitle;
  }, [businessMainIndustryQuestion?.title, businessMainIndustryTitle]);
  const [businessTradeOption, setBusinessTradeOption] = useState<QuestionOption | null>(null);
  const isTradeQuestionLoading = getOnboardingFormStatus === 'pending';

  useEffect(() => {
    if (linkIsInvalid) {
      setIsValidatingToken(false);
      return;
    }

    // Prevent duplicate API calls
    if (hasValidatedRef.current) {
      setIsValidatingToken(false);
      return;
    }

    hasValidatedRef.current = true;
    setIsValidatingToken(true);

    validateRegistrationToken(decodedEmail, registrationCode)
      .then((result) => {
        const expired = !result.valid;
        setIsTokenExpired(expired);
        setShowExpiredModal(expired);
      })
      .catch((error) => {
        logger.error('Failed to validate registration token', error);
        setIsTokenExpired(true);
        setShowExpiredModal(true);
      })
      .finally(() => {
        setIsValidatingToken(false);
      });
  }, [decodedEmail, registrationCode, linkIsInvalid, logger]);

  useEffect(() => {
    if (linkIsInvalid || isTokenExpired) {
      return;
    }

    // Skip API call if we already have valid data
    if (businessInformationQuestions.length > 0) {
      return;
    }

    dispatch(getOnboardingForm({
      formId: ONBOARDING_FORMS.businessInformation.id,
      isPublic: true,
      trackRequest,
    }))
      .then(unwrapResult)
      .then((response) => {
        if (!response?.questions || response?.questions.length === 0) {
          throw new Error('No questions found');
        }
      })
      .catch((e) => {
        logger.error('Failed to load business information onboarding form', e);
        setErrorMessage(t('error.generalMessage3'));
        setOpenErrorSnackbar(true);
      });
  }, [
    logger,
    t,
    dispatch,
    trackRequest,
    linkIsInvalid,
    isTokenExpired,
    businessInformationQuestions.length,
  ]);

  useEffect(() => {
    if (!businessMainIndustryQuestion?.options) {
      return;
    }

    if (!formValues.trade) {
      if (businessTradeOption) {
        setBusinessTradeOption(null);
      }
      return;
    }

    const matchedOption = businessMainIndustryQuestion.options.find(
      (option) => option.value === formValues.trade,
    );

    if (matchedOption &&
      (!businessTradeOption || businessTradeOption.value !== matchedOption.value)) {
      setBusinessTradeOption(matchedOption);
    }
  }, [businessMainIndustryQuestion?.options, businessTradeOption, formValues.trade]);

  useEffect(() => {
    if (countdown === null) {
      return undefined;
    }

    if (countdown === 0) {
      navigate('/login');
      return undefined;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [countdown, navigate]);

  const handleBusinessTradeChange = (...args: unknown[]) => {
    const [value] = args;
    const selectedOption = (value as QuestionOption | null) || null;
    if (!selectedOption) {
      setBusinessTradeOption(null);
      setFormValues((prev) => ({ ...prev, trade: '' }));
      setFormErrors((prev) => ({ ...prev, trade: undefined }));
      return;
    }

    setBusinessTradeOption(selectedOption);
    setFormValues((prev) => ({ ...prev, trade: selectedOption.value || '' }));
    setFormErrors((prev) => ({ ...prev, trade: undefined }));
  };
  const handleSupportNavigation = () => {
    if (solutionOrg) {
      navigate(`/contact-support?solutionOrg=${solutionOrg}`);
    } else {
      navigate('/contact-support');
    }
  };

  const handleDifferentEmail = () => {
    navigate('/signup');
  };

  const handleResendEmail = async () => {
    setIsResendingEmail(true);
    try {
      await memberRegistration({ email: decodedEmail });
      // Don't close the modal - just update the state to show success message
      setEmailResent(true);
    } catch (error) {
      logger.error('Failed to resend registration email', error);
      setErrorMessage(t('error.generalMessage'));
      setOpenErrorSnackbar(true);
    } finally {
      setIsResendingEmail(false);
    }
  };

  const validateFields = () => {
    const errors: Partial<typeof formValues> = {};
    const requiredMsg = t('error.fieldRequired');

    if (!formValues.firstName.trim()) errors.firstName = requiredMsg;
    if (!formValues.lastName.trim()) errors.lastName = requiredMsg;
    if (!formValues.companyName.trim()) errors.companyName = requiredMsg;
    if (!formValues.trade.trim()) errors.trade = requiredMsg;

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildRequestPayload = () => ({
    password: registrationCode.trim(),
    memberInput: {
      firstName: formValues.firstName.trim(),
      lastName: formValues.lastName.trim(),
      email: decodedEmail,
    },
    companyInput: {
      name: formValues.companyName.trim(),
      addressStreet: '',
      addressCity: '',
      addressState: '',
      addressCountry: DEFAULT_COUNTRY,
      addressZipCode: '',
      fullAddress: '',
      email: decodedEmail,
      businessMainIndustry: formValues.trade.trim(),
    },
  });

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    setErrorMessage(null);

    if (!validateFields()) return;

    setIsSubmitting(true);

    try {
      const { tempAccessToken, source } = await publicMemberValidation(buildRequestPayload());

      setOpenSuccessSnackbar(true);
      trackSignUp('Auth', 'Validation', 'Member', {
        email: decodedEmail,
        registrationSource: source || RegistrationSource.MARKETING,
      });
      setTimeout(() => {
        navigate(
          `${AUTO_LOGIN_ROUTE}?email=${encodeURIComponent(decodedEmail)}` +
          `&accessToken=${encodeURIComponent(tempAccessToken)}` +
          `&afterLoginPath=${encodeURIComponent(onboardingRedirectPath)}` +
          '&loginSource=registration',
        );
      }, 1200);
    } catch (err) {
      logger.error(Logger.format(err));
      const axiosError = err as AxiosError<{ message?: string }>;
      const statusCode = axiosError?.response?.status;

      // Treat all 400/403 errors as invalid/expired link errors
      // since this endpoint only validates registration links
      if (statusCode === 400 || statusCode === 403) {
        setErrorMessage(t('signUp.invalidLink'));
        setOpenErrorSnackbar(true);
        setCountdown(3);
      } else {
        // Generic error for unexpected 500 error
        setErrorMessage(t('error.generalMessage'));
        setOpenErrorSnackbar(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSnackbarClose = () => {
    setOpenErrorSnackbar(false);
    setOpenSuccessSnackbar(false);
  };

  useEffect(() => {
    if (linkIsInvalid) {
      setCountdown(3);
    }
  }, [linkIsInvalid]);

  if (isValidatingToken) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          background: theme.palette.common.white,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100vh',
          pr: theme.spacing(3),
          pl: theme.spacing(3),
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}>
          <CircularProgress color="inherit" size={40} />
        </Box>
      </Container>
    );
  }

  if (linkIsInvalid) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          background: theme.palette.common.white,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100vh',
          pr: theme.spacing(3),
          pl: theme.spacing(3),
        }}
      >
        <Box sx={{ textAlign: 'left' }}>
          {Logo && <img src={Logo as string} alt="Logo" style={{ width: theme.spacing(20.625) }} />}
        </Box>
        <Box sx={{ mt: theme.spacing(6) }}>
          <Alert severity="error">
            {t('signUp.invalidLink')}
            {countdown !== null && ` ${t('signUp.redirecting')} ${countdown}s...`}
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="sm"
      sx={{
        background: theme.palette.common.white,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100vh',
        pr: theme.spacing(3),
        pl: theme.spacing(3),
      }}
    >
      <>
        <Snackbar
          open={openSuccessSnackbar}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Alert severity="success" sx={{ width: '100%' }}>
            {t('success.signUp')}
          </Alert>
        </Snackbar>
        <Snackbar
          open={openErrorSnackbar}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            {errorMessage || t('error.generalMessage')}
            {countdown !== null && ` ${t('signUp.redirecting')} ${countdown}...`}
          </Alert>
        </Snackbar>
        <Box sx={{ textAlign: 'left' }}>
          {Logo && <img src={Logo as string} alt="Logo" style={{ width: theme.spacing(20.625) }} />}
          {isImproveIt360 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mt: 1,
                mb: 1,
              }}
            >
              <Typography
                variant="body1"
                sx={{ mr: 1, color: theme.palette.grey[600] }}
              >
                {t('login.broughtToYouBy')}
              </Typography>
              <img
                src={improveIt360Logo}
                alt="Improve It 360 Logo"
              />
            </Box>
          )}
        </Box>
        <Box
          sx={{
            marginTop: theme.spacing(4),
          }}
        >
          <Typography
            sx={{
              textAlign: 'left',
              mb: theme.spacing(1),
            }}
            variant="h4"
          >
            {t('signUp.heading')}
          </Typography>

          <Box
            component="form"
            sx={{ mt: theme.spacing(3) }}
            onSubmit={handleSubmit}
          >
            <OptimusInput
              label={t('signUp.emailLabel') || 'Email'}
              value={decodedEmail}
              disabled
              size="m"
              width="100%"
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(2),
                mt: theme.spacing(2),
              }}
            >
              <OptimusInput
                data-testid={TestIds.signup.firstNameInput}
                label={t('signUp.firstNameLabel') || ''}
                required
                placeholderText={t('signUp.firstNamePlaceholder') as string}
                value={formValues.firstName}
                onInputChange={(event) => {
                  setFormValues((prev) => ({ ...prev, firstName: event.target.value }));
                  setFormErrors((prev) => ({ ...prev, firstName: undefined }));
                }}
                error={!!formErrors.firstName}
                helperText={formErrors.firstName}
                size="m"
                width="100%"
                disabled={isTokenExpired}
              />
              <OptimusInput
                data-testid={TestIds.signup.lastNameInput}
                label={t('signUp.lastNameLabel') || ''}
                required
                placeholderText={t('signUp.lastNamePlaceholder') as string}
                value={formValues.lastName}
                onInputChange={(event) => {
                  setFormValues((prev) => ({ ...prev, lastName: event.target.value }));
                  setFormErrors((prev) => ({ ...prev, lastName: undefined }));
                }}
                error={!!formErrors.lastName}
                helperText={formErrors.lastName}
                size="m"
                width="100%"
                disabled={isTokenExpired}
              />
              <OptimusInput
                data-testid={TestIds.signup.companyNameInput}
                label={t('signUp.companyNameLabel') || ''}
                required
                placeholderText={t('signUp.companyNamePlaceholder') as string}
                value={formValues.companyName}
                onInputChange={(event) => {
                  setFormValues((prev) => ({ ...prev, companyName: event.target.value }));
                  setFormErrors((prev) => ({ ...prev, companyName: undefined }));
                }}
                error={!!formErrors.companyName}
                helperText={formErrors.companyName}
                size="m"
                width="100%"
                disabled={isTokenExpired}
              />
              <Box sx={{ width: '100%' }}>
                {canSelectBusinessTrade ? (
                  <>
                    <Typography
                      sx={{
                        fontSize: theme.spacing(1.75),
                        fontWeight: 500,
                        mb: theme.spacing(0.75),
                        textAlign: 'left',
                      }}
                    >
                      {t('signUp.tradeLabel') || ''}
                      <Box
                        component="span"
                        sx={{ color: theme.palette.error.main, ml: theme.spacing(0.0625) }}
                      >
                        *
                      </Box>
                    </Typography>
                    <Box sx={{ pointerEvents: isTokenExpired ? 'none' : 'auto', opacity: isTokenExpired ? 0.6 : 1 }}>
                      <QuestionComponent
                        data-testid={TestIds.signup.tradeInput}
                        type={businessMainIndustryQuestion?.type || 'multichoice'}
                        placeholderText={tradePlaceholderKey}
                        meta={businessMainIndustryQuestion?.meta || { behavior: 'autoComplete' }}
                        options={businessMainIndustryOptions}
                        inputValue={businessTradeOption}
                        onChange={handleBusinessTradeChange}
                        calledFromOnboarding
                        showAutoCompletePlaceholderText
                      />
                    </Box>
                  </>
                ) : (
                  <OptimusInput
                    data-testid={TestIds.signup.tradeInput}
                    label={t('signUp.tradeLabel') || ''}
                    required
                    placeholderText={t('signUp.tradePlaceholder') as string}
                    value={formValues.trade}
                    onInputChange={(event) => {
                      setFormValues((prev) => ({ ...prev, trade: event.target.value }));
                      setFormErrors((prev) => ({ ...prev, trade: undefined }));
                    }}
                    error={!!formErrors.trade}
                    helperText={formErrors.trade}
                    size="m"
                    width="100%"
                    disabled={
                      isTokenExpired ||
                      isTradeQuestionLoading
                    }
                  />
                )}
                {formErrors.trade && canSelectBusinessTrade && (
                  <Typography
                    variant="body8"
                    sx={{
                      color: theme.palette.error.main,
                      display: 'block',
                      mt: theme.spacing(1),
                      textAlign: 'left',
                    }}
                  >
                    {formErrors.trade}
                  </Typography>
                )}
              </Box>
            </Box>
            <OptimusButton
              data-testid={TestIds.signup.completeRegistrationBtn}
              fullWidth
              size="m"
              sx={{
                mb: theme.spacing(0),
                mt: theme.spacing(3),
              }}
              disabled={isSubmitting || isTokenExpired}
              type="submit"
              buttonText={isSubmitting ? (
                <CircularProgress color="inherit" size={24} />
              ) : (t('signUp.submitButton') || '')}
            />
            <OptimusButton
              data-testid={TestIds.signup.editEmailLnk}
              size="m"
              customVariant="secondaryGray"
              fullWidth
              sx={{
                mb: theme.spacing(2),
                mt: theme.spacing(2),
              }}
              disabled={isSubmitting || isTokenExpired}
              type="button"
              onClick={handleDifferentEmail}
              buttonText={t('signUp.editEmail') || ''}
            />
          </Box>
        </Box>
        <Box
          sx={{
            mt: theme.spacing(3),
            textAlign: 'center',
          }}
        >
          <Typography variant="body7">
            {t('signUp.signUpTroubleMessage')}
            &nbsp;
            <Link
              onClick={handleSupportNavigation}
              sx={{
                cursor: 'pointer',
              }}
              data-testid={TestIds.login.contactSupportLnk}
              variant="h6"
            >
              {t('login.contactSupport')}
            </Link>
          </Typography>
        </Box>

        <Dialog
          open={showExpiredModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: theme.spacing(1),
              padding: 0,
            },
          }}
        >
          <DialogTitle
            sx={{ padding: theme.spacing(1.5, 2.5, 1.5, 3), ...theme.typography.headingSm }}
          >
            {t('signUp.expiredLinkTitle')}
          </DialogTitle>
          <DialogContent sx={{ padding: theme.spacing(2.5, 3, 3, 3) }}>
            {isResendingEmail && (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: theme.spacing(5.25),
              }}>
                <CircularProgress color="inherit" size={16} />
              </Box>
            )}
            {!isResendingEmail && emailResent && (
              <Typography
                variant="body7"
                sx={{
                  color: theme.palette.grey[800],
                  lineHeight: '21px',
                }}
              >
                {t('signUp.emailResentMessage', { email: decodedEmail }) || ''}
                <br />
                <br />
                {t('signUp.closeTabMessage') || ''}
              </Typography>
            )}
            {!isResendingEmail && !emailResent && (
              <Typography
                variant="body7"
                sx={{
                  color: theme.palette.grey[800],
                }}
              >
                {t('signUp.expiredLinkMessage')}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ padding: theme.spacing(2, 3), justifyContent: 'center' }}>
            <OptimusButton
              fullWidth
              size="m"
              onClick={handleResendEmail}
              disabled={isResendingEmail || emailResent}
              buttonText={t('button.resendEmail') || ''}
            />
          </DialogActions>
        </Dialog>
      </>
    </Container>
  );
}
